import { computed, inject, provide, ref, watch } from "vue";
import { useStore } from "vuex";

import { EmailQuoteOrInvoiceProps, EmailRecipient } from "./types";

export const injectKey = Symbol();

export function useEmailQuoteOrInvoice(provideState = false) {
  const quote = ref<EmailQuoteOrInvoiceProps["quote"]>();
  const invoice = ref<EmailQuoteOrInvoiceProps["invoice"]>();
  const store = useStore();
  const isDirty = ref(false);
  const errorMessages = ref({
    onePrimaryOrAddressRequired: ""
  });
  const defaultRecipients = ref<EmailRecipient[]>([]);
  const additionalRecipients = ref<string[]>([]);
  const additionalRecipientsElRef = ref<
    { validate: () => Promise<boolean> }[] | null
  >(null);

  const selectedEmailAddresses = computed(() => {
    const defaultRecipientsEmail = defaultRecipients.value
      .filter((recipient) => recipient.checked)
      .map((recipient) => recipient.email_address);
    const additionalRecipientsEmail = additionalRecipients.value;
    return {
      default: defaultRecipientsEmail,
      additional: additionalRecipientsEmail
    };
  });

  const customerId = computed(() => {
    if (quote.value) return quote.value.customer_id;
    if (invoice.value) return invoice.value.customer.customer_id;
    return undefined;
  });

  const addressRecipients = computed(() => {
    return defaultRecipients.value.filter(
      (recipient) => recipient.owner === "Address"
    );
  });

  const primaryRecipients = computed(() => {
    return defaultRecipients.value.filter(
      (recipient) => recipient.owner === "Customer"
    );
  });

  async function init(params: EmailQuoteOrInvoiceProps) {
    let emailRecipients: EmailRecipient[] = [];
    quote.value = params.quote;
    invoice.value = params.invoice;
    if (params.quote) {
      emailRecipients = await store.dispatch(
        "getEmailRecipientsForQuote",
        params.quote
      );
    } else {
      emailRecipients = await store.dispatch("getEmailRecipientsForInvoice", {
        id: params.invoice.invoice_id,
        customer_id: params.invoice.customer.customer_id
      });
    }
    defaultRecipients.value = emailRecipients;
  }

  function handleToggleRecipient(recipient: EmailRecipient) {
    recipient.checked = !recipient.checked;
  }

  function handleAddAdditionalRecipient() {
    additionalRecipients.value.push("");
  }

  function clearErrorMessages() {
    Object.keys(errorMessages.value).forEach((key) => {
      errorMessages.value[key] = "";
    });
  }

  function validateErrorMessages() {
    clearErrorMessages();
    if (!isDirty.value) return;
    if (selectedEmailAddresses.value.default.length === 0) {
      errorMessages.value.onePrimaryOrAddressRequired =
        "At least one primary or address recipient is required";
    }
  }

  function scrollToNearestError() {
    setTimeout(() => {
      const nearestError =
        document.querySelector(".error") ||
        document.querySelector(".text-danger");
      if (!nearestError) return;
      nearestError.scrollIntoView({ behavior: "smooth" });
    }, 500);
  }

  async function validate() {
    let additionalRecipientsValid = true;
    isDirty.value = true;
    validateErrorMessages();
    if (additionalRecipientsElRef.value) {
      additionalRecipientsValid = (
        await Promise.all(
          additionalRecipientsElRef.value!.map(async (el) => el.validate())
        )
      ).every((result) => result === true);
    }
    scrollToNearestError();
    return (
      additionalRecipientsValid &&
      Object.values(errorMessages.value).every((val) => val == "")
    );
  }

  function handleRemoveAdditionalRecipient(index: number) {
    additionalRecipients.value.splice(index, 1);
  }

  watch(selectedEmailAddresses, () => {
    validateErrorMessages();
  });

  const emailQuoteOrInvoiceInterface = {
    quote,
    invoice,
    customerId,
    init,
    additionalRecipientsElRef,
    defaultRecipients,
    additionalRecipients,
    errorMessages,
    addressRecipients,
    primaryRecipients,
    selectedEmailAddresses,
    validate,
    handleAddAdditionalRecipient,
    handleRemoveAdditionalRecipient,
    handleToggleRecipient
  };

  if (provideState) provide(injectKey, emailQuoteOrInvoiceInterface);

  return emailQuoteOrInvoiceInterface;
}

export const injectUseEmailQuoteOrInvoice = () =>
  inject<ReturnType<typeof useEmailQuoteOrInvoice>>(injectKey)!;
